import React, { useState } from 'react';
import { TextField, Button, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import AmberSectionTitle from '../labels/AmberSectionTitle';
import { getTwoFactorEnforcementDate } from '../../helpers/twoFactorAuthentication';

const CODE_LENGTH = 6;

const useStyles = makeStyles((theme) => ({
  form: {
    width: 330,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  submit: {
    marginTop: theme.spacing(2),
    height: theme.spacing(6),
    fontSize: '1rem',
    fontWeight: 800,
  },
  secondaryAction: {
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  // Settings page: QR code and key on the left, steps and code on the right
  wideForm: {
    width: '100%',
    display: 'grid',
    // The left column takes the width of the key, so nothing wraps on wide screens
    gridTemplateColumns: 'max-content 1fr',
    columnGap: theme.spacing(5),
    textAlign: 'left',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      rowGap: theme.spacing(3),
      textAlign: 'center',
    },
  },
  aside: {
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  qrCode: {
    margin: theme.spacing(2, 0),
  },
  qrFrame: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    lineHeight: 0,
    marginBottom: theme.spacing(2),
  },
  secretKey: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    backgroundColor: theme.palette.grey[100],
    fontFamily: 'Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: '0.85rem',
    letterSpacing: 1,
    wordBreak: 'break-all',
    userSelect: 'all',
  },
  mailIcon: {
    fontSize: 56,
    marginBottom: theme.spacing(1),
  },
  email: {
    fontWeight: 600,
    wordBreak: 'break-all',
  },
  paragraph: {
    marginTop: theme.spacing(2),
  },
  help: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const sanitizeCode = (value) => value.replace(/\D/g, '').slice(0, CODE_LENGTH);

function TotpCodeForm({
  invalidCode,
  onSubmit,
  secondaryActionText,
  onSecondaryAction,
  children,
  aside,
}) {
  const classes = useStyles();
  const [t] = useTranslation();
  const [code, setCode] = useState('');

  const fields = (
    <>
      <TextField
        margin="normal"
        fullWidth
        autoFocus
        id="totp-code"
        name="totp-code"
        variant="outlined"
        label={t('loginForm.twoFactor.codeLabel')}
        value={code}
        onChange={(e) => setCode(sanitizeCode(e.target.value))}
        error={invalidCode}
        helperText={invalidCode ? t('loginForm.twoFactor.invalidCode') : ''}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          'data-testid': 'totp-code-input',
        }}
      />
      <Button
        className={classes.submit}
        type="submit"
        fullWidth
        color="primary"
        variant="contained"
        disabled={code.length !== CODE_LENGTH}
        data-testid="totp-submit-btn"
      >
        {t('loginForm.twoFactor.verifyButton')}
      </Button>
      <Button
        className={classes.secondaryAction}
        onClick={onSecondaryAction}
        data-testid="totp-secondary-btn"
      >
        {secondaryActionText}
      </Button>
      <Typography variant="caption" className={classes.help}>
        {t('loginForm.twoFactor.lostDevice')}
      </Typography>
    </>
  );
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(code);
  };

  if (aside) {
    return (
      <form className={classes.wideForm} onSubmit={handleSubmit}>
        <div className={classes.aside}>{aside}</div>
        <div className={classes.main}>
          {children}
          {fields}
        </div>
      </form>
    );
  }
  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      {children}
      {fields}
    </form>
  );
}

TotpCodeForm.propTypes = {
  invalidCode: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  secondaryActionText: PropTypes.string.isRequired,
  onSecondaryAction: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  aside: PropTypes.node,
};

TotpCodeForm.defaultProps = {
  aside: null,
};

export function TotpChallengeStep({ invalidCode, onSubmit, onCancel }) {
  const [t] = useTranslation();

  return (
    <TotpCodeForm
      invalidCode={invalidCode}
      onSubmit={onSubmit}
      secondaryActionText={t('loginForm.twoFactor.cancel')}
      onSecondaryAction={onCancel}
    >
      <AmberSectionTitle
        typographyVariant="h5"
        text={t('loginForm.twoFactor.challengeTitle')}
      />
      <Typography variant="body2">
        {t('loginForm.twoFactor.challengeDescription')}
      </Typography>
    </TotpCodeForm>
  );
}

TotpChallengeStep.propTypes = {
  invalidCode: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export function TotpEnrolmentStep({
  secretKey,
  qrCodeUrl,
  required,
  invalidCode,
  onSubmit,
  onSkip,
  onCancel,
  skipText,
  wide,
}) {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const enforcementDate = getTwoFactorEnforcementDate();

  const getRequirementText = () => {
    if (required) {
      return t('loginForm.twoFactor.enrolmentRequired');
    }
    if (enforcementDate) {
      return t('loginForm.twoFactor.enrolmentRequiredFrom', {
        date: enforcementDate.toLocaleDateString(i18n.language),
      });
    }
    return t('loginForm.twoFactor.enrolmentRecommended');
  };

  const qrCodeAndKey = (
    <>
      <div className={wide ? classes.qrFrame : classes.qrCode}>
        <QRCodeSVG value={qrCodeUrl} size={180} data-testid="totp-qr-code" />
      </div>
      <Typography variant="caption">
        {t('loginForm.twoFactor.enrolmentManualKey')}
      </Typography>
      <code className={classes.secretKey} data-testid="totp-secret-key">
        {secretKey}
      </code>
    </>
  );

  return (
    <TotpCodeForm
      aside={wide ? qrCodeAndKey : null}
      invalidCode={invalidCode}
      onSubmit={onSubmit}
      secondaryActionText={
        required
          ? t('loginForm.twoFactor.cancel')
          : skipText || t('loginForm.twoFactor.skip')
      }
      onSecondaryAction={required ? onCancel : onSkip}
    >
      <AmberSectionTitle
        typographyVariant="h5"
        text={t('loginForm.twoFactor.enrolmentTitle')}
      />
      <Typography variant="body2" data-testid="totp-requirement-text">
        {getRequirementText()}
      </Typography>
      <Box marginTop={2}>
        <Typography variant="body2">
          {t('loginForm.twoFactor.enrolmentScan')}
        </Typography>
      </Box>
      {!wide && qrCodeAndKey}
      <Box marginTop={2}>
        <Typography variant="body2">
          {t('loginForm.twoFactor.enrolmentEnterCode')}
        </Typography>
      </Box>
    </TotpCodeForm>
  );
}

TotpEnrolmentStep.propTypes = {
  secretKey: PropTypes.string.isRequired,
  qrCodeUrl: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  invalidCode: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  skipText: PropTypes.string,
  wide: PropTypes.bool,
};

TotpEnrolmentStep.defaultProps = {
  skipText: null,
  wide: false,
};

/**
 * Shown after a login stopped because 2FA is required but Firebase only allows
 * setting it up with a verified email. The verification link was just sent.
 */
export function VerifyEmailStep({ email, onBackToLogin }) {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <Box className={classes.form} data-testid="verify-email-step">
      <MailOutlineIcon color="primary" className={classes.mailIcon} />
      <AmberSectionTitle
        typographyVariant="h5"
        text={t('loginForm.twoFactor.verifyEmail.title')}
      />
      <Typography variant="body2">
        {t('loginForm.twoFactor.verifyEmail.description')}
      </Typography>
      <Typography variant="body2" className={classes.paragraph}>
        {t('loginForm.twoFactor.verifyEmail.linkSent')}
      </Typography>
      <Typography
        variant="body1"
        className={classes.email}
        data-testid="verify-email-address"
      >
        {email}
      </Typography>
      <Typography variant="body2" className={classes.paragraph}>
        {t('loginForm.twoFactor.verifyEmail.afterVerifying')}
      </Typography>
      <Button
        className={classes.submit}
        fullWidth
        color="primary"
        variant="contained"
        onClick={onBackToLogin}
        data-testid="verify-email-back-btn"
      >
        {t('loginForm.twoFactor.verifyEmail.backToLogin')}
      </Button>
      <Typography variant="caption" className={classes.help}>
        {t('loginForm.twoFactor.verifyEmail.noEmail')}
      </Typography>
    </Box>
  );
}

VerifyEmailStep.propTypes = {
  email: PropTypes.string.isRequired,
  onBackToLogin: PropTypes.func.isRequired,
};
